import { inject, Injectable } from '@angular/core';
import { Auth, } from "@angular/fire/auth";
import { confirmPasswordReset } from 'firebase/auth';
import { BehaviorSubject, from, Observable } from "rxjs";
import { SessionKeys, StorageKeys, StorageType, ValidRoles } from "../../shared/enums";
import { StorageService } from "@shared/services/storage/storage.service";
import { Profile } from '@shared/interfaces/perfil.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private auth = inject(Auth);
  private storage: StorageService = inject(StorageService);

  userRole: BehaviorSubject<string> = new BehaviorSubject<string>('');
  userPermissions: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  permissionsLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    this.checkRolesStatus();
  }

  checkRolesStatus() {
    if (this.userRole.value === '') {
      const roleInfo = this.storage.getItem<Profile | null>(SessionKeys.PROFILE, StorageType.SESSION);
      if (roleInfo) {
        this.userRole.next(roleInfo.rol);
        this.userPermissions.next(roleInfo.permisos as string[]);
      }
    }
  }

  confirmPasswordReset(oobCode: string,
    newPassword: string): Observable<void> {
    return from(confirmPasswordReset(this.auth, oobCode, newPassword));
  }

  setAuthenticationInfo(authInfo: any) {
    this.storage.setItem(SessionKeys.PROFILE, authInfo, StorageType.SESSION);
    this.userPermissions.next(authInfo.permisos);
    this.userRole.next(authInfo.rol);
    this.permissionsLoaded.next(true);
  }

  hasAccess(permissions: string[], mustHaveAll: boolean = false): boolean {
    if (!permissions?.length) {
      return true;
    }

    const userPerms: string[] = this.userPermissions.value;
    if (!userPerms?.length) {
      return false;
    }

    const userPermSet = new Set(userPerms.map(p => p.toLowerCase()));

    return mustHaveAll
      ? permissions.every(p => userPermSet.has(p.toLowerCase()))
      : permissions.some(p => userPermSet.has(p.toLowerCase()));
  }

  hasRole(role: ValidRoles): boolean {
    return this.userRole.value === role;
  }

  hasAtLeastOneRole(roles: ValidRoles[]): boolean {
    return roles.includes(this.userRole.value as ValidRoles);
  }

  isSelectedMedicalCenter(): boolean {
    return this.storage.getItem(StorageKeys.CURRENT_MEDICAL_CENTER, StorageType.LOCAL) !== null;
  }

  reset(): void {
    this.userRole.next('');
    this.userPermissions.next([]);
    this.permissionsLoaded.next(false);
  }
}
